import * as React from "react"
import { forwardRef, Ref } from "react"
import { Rect } from "../../types/Rect"
import { Size } from "../../types/Size"
import { WithEvents, WithEventsProperties } from "../../../components/hoc/WithEvents"
import { DeprecatedFrame, DeprecatedCoreFrameProps } from "./DeprecatedFrame"
import { FrameWithMotion, FrameProps } from "./FrameWithMotion"
import { isAnimatable } from "../../../animation/Animatable/Animatable"
import { useParentSize, deprecatedParentSize } from "../../types/NewConstraints"

// Re-exports
export { DeprecatedFrame, DeprecatedCoreFrameProps, DeprecatedFrameProperties } from "./DeprecatedFrame"
export { BackgroundImageState, FrameWithMotion, FrameProps } from "./FrameWithMotion"
export { BaseFrameProps, FrameLayoutProperties, CSSTransformProperties, VisualProperties } from "./types"

// Private types
/**
 * We merge these props to combine them with events
 */

// Public types
/** @public */
export type DeprecatedFrameWithEventsProps = DeprecatedCoreFrameProps & WithEventsProperties
/** @public */
export const DeprecatedFrameWithEvents: React.ComponentClass<Partial<DeprecatedFrameWithEventsProps>> = WithEvents(
    DeprecatedFrame
)

type FrameWithMotionPropsKey = keyof FrameProps
const frameWithMotionPropsFields: FrameWithMotionPropsKey[] = [
    "_border",
    "_constraints",
    "animate",
    "initial",
    "variants",
    "transition",
    "inherit",
    "static",
]

type DeprecatedFramePropsKey = keyof DeprecatedCoreFrameProps
const deprecatedFramePropsFields: DeprecatedFramePropsKey[] = [
    "autoSize",
    "aspectRatio",
    "borderWidth",
    "borderStyle",
    "borderColor",
    "centerX",
    "centerY",
]

type CommonFramePropsKey = FrameWithMotionPropsKey & DeprecatedFramePropsKey
// Better to sort this list by approximate usage frequency
const commonFramePropsFields: CommonFramePropsKey[] = ["width", "height", "left", "right", "top", "bottom"]

/**
 * This function guard will define is props meant to be used with old frame or with new one
 * @param props Old or new props for the frame
 */
export function isDeprecatedFrameProps(props: any): props is Partial<DeprecatedFrameWithEventsProps> {
    let field: string | number
    // If we are using animatables, definitely use a DeprecatedFrame
    for (const propKey in props) {
        if (isAnimatable(props[propKey])) return true
    }

    // It is a new frame API props
    for (field of frameWithMotionPropsFields) {
        if (props.hasOwnProperty(field)) return false
    }

    // It is definitely an old API
    for (field of deprecatedFramePropsFields) {
        if (props.hasOwnProperty(field)) return true
    }

    // Fallback to new Frame API
    return false
}

// const isPreview = RenderEnvironment.target === RenderTarget.preview

// We need switcher component to useContext without conditions
// THIS SHOULD NOT BE USED DIRECTLY IN LIBRARY NOR IN VEKTER
// Only for backwards compatibility
/** @public */
// tslint:disable-next-line:no-shadowed-variable
export const Frame = forwardRef(function Frame(props: Partial<FrameProps>, ref: Ref<HTMLDivElement>) {
    const parentSize = useParentSize()
    if (isDeprecatedFrameProps(props)) {
        const currentParentSize: Size | null = props.parentSize || deprecatedParentSize(parentSize)

        // We use here DeprecatedFrame WithEvents for simplicity
        return <DeprecatedFrameWithEvents {...props} parentSize={currentParentSize} />
    }
    return <FrameWithMotion {...props} ref={ref} />
})
;(Frame as any).rect = (props: Partial<FrameProps>, parentSize?: Size): Rect | null => {
    if (isDeprecatedFrameProps(props)) {
        return DeprecatedFrame.rect(props)
    }
    return FrameWithMotion.rect(props)
}
;(Frame as any).name = "Frame"
